import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { ReactComponent as DefaultVehicleIcon } from '../../../../../assets/icons/car.svg';
import { ReactComponent as ClockIcon } from '../../../../../assets/icons/clock-icon-v2.svg';
import { ReactComponent as TrashIcon } from '../../../../../assets/icons/delete-icon.svg';
import { ReactComponent as PowerIcon } from '../../../../../assets/icons/power.svg';
// import { ReactComponent as VehicleIcon } from '../../../../../assets/icons/vehicle.svg';
import { useAppBranding } from '../../../../../config/branding';
import { useAppSelector } from '../../../../../redux/hooks';
import { fetchBasicInformations } from '../../../../../redux/slices/EndConsumerSlice';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../../shared-components/button/Button';
import CustomDropDown from '../../../../../shared-components/custom-drop-down/CustomDropDown';
import DeleteWarning from '../../../../../shared-components/delete-warning/DeleteWarning';
import ConfirmBox from '../../../../../shared-components/modal-box/ConfirmBox';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../../../../shared-components/switch-button-field/SwitchButton';
import SidePaneModel from '../../../../../shared-components/table-side-panel/SidePaneModel';
import FormatDuration from '../../../../../shared-functions/FormatDuration';
import ToFixedValue from '../../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../../utils/api/end-consumer-v2';
import emspSessionApi from '../../../../../utils/api/v2/emsp-session';
import SessionDetails from '../../EndConsumerProfile/SessionDetails';

interface ChargingDetailsInterface {
  remainingMinutes: number;
  totalDurationInMinutes: number;
  totalHours: number;
  totalPowerConsumedInKwh: number;
  totalSessions: number;
  totalSessionAmount: number;
}

interface VehicleSessionCountInterface {
  vehicleId: string;
  commercialCount: number;
  personalCount: number;
  totalCount: number;
  powerConsumedInKwh: number;
  durationSeconds: number;
}

interface VehicleDetailsInterface {
  batteryCapacity?: string;
  commercialChargingDetails?: ChargingDetailsInterface;
  connectorType?: string;
  createdAt?: string;
  deletedAt?: string;
  isFavourite?: boolean;
  isSelected?: boolean;
  manufacturer?: string;
  model?: string;
  personalChargingDetails?: ChargingDetailsInterface;
  updatedAt?: string;
  userCustomId?: string;
  vehicleImageUrl?: string;
  vehicleImageUrls?: {
    _id?: string;
    url?: string;
    fileName?: string;
    fileType?: string;
    fileSize?: string;
  }[];
  _id: string;
}

/**
 * MyVehicle component
 * @returns {any} - A React component for displaying vehicle information.
 */
function MyVehicle() {
  const branding = useAppBranding();
  const dispatch = useDispatch();
  const { t } = useTranslation('endConsumer');
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const { selectedEndConsumer, basicInformation } = useAppSelector(
    (state) => state.endConsumerType,
  );
  const [vehicleList, setVehicleList] = useState<VehicleDetailsInterface[]>([]);
  const [sessionCounts, setSessionCounts] = useState<{
    [vehicleId: string]: {
      commercialCount: number;
      personalCount: number;
      totalCount: number;
      powerConsumedInKwh: number;
      durationSeconds: number;
    };
  }>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [modalView, setModalView] = useState({
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });
  const [confirmBox, setConfirmBox] = useState({
    status: false,
    title: '',
    subTitle: '',
    submitLabel: t('ok'),
    /**
     * Demo function
     */
    conformSubmit: () => {},
  });
  const [isModelOpen, setIsModelOpen] = useState<boolean>(false);
  const [modelData, setModelData] = useState({
    title: '',
    content: <div />,
  });

  /**
   * Set primary vehicle function
   * @param {any} key - The key of the toggle to activate.
   * @param {any} index - The index of the vehicle in the data array.
   */
  const setPrimaryVehicle = async (key: string, index: number) => {
    setIsLoading(true);
    try {
      await endConsumerApi.setVehiclePrimary(selectedEndConsumer?.userCustomId || '', {
        vehicleId: vehicleList[index]._id,
      });
      if (selectedEndConsumer?.userCustomId) {
        await dispatch(
          fetchBasicInformations({ userCustomId: selectedEndConsumer.userCustomId }) as any,
        );
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message,
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
    setOpenStatus(true);
    setModalView({
      title: '',
      header: false,
      content: (
        <SuccessNotification
          title={t('vehicleSetAsPrimarySuccessfully')}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={() => {
            setVehicleList((previousData) =>
              previousData.map((vehicle, vehicleIndex) =>
                vehicleIndex === index
                  ? {
                      ...vehicle,
                      isSelected: key === 'isSelected',
                    }
                  : {
                      ...vehicle,
                      isSelected: false,
                    },
              ),
            );
            setOpenStatus(false);
          }}
        />
      ),
      rightCorner: '',
      notification: true,
    });
  };

  /**
   * Handle toggle switch change.
   * @param {any} key - The key of the toggle to activate.
   * @param {any} index - The index of the vehicle in the data array.
   */
  const handleToggleSwitch = (key: string, index: number) => {
    setConfirmBox({
      status: true,
      title: t('setAsPrimary'),
      subTitle: t('areYouSureYouWantToMakeThisCarAsAPrimary'),
      /**
       * Set primary vehicle function
       */
      conformSubmit: () => {
        setPrimaryVehicle(key, index);
        setConfirmBox({ ...confirmBox, status: false });
      },
      submitLabel: t('ok'),
    });
  };

  /**
   * Close modal box function
   */
  const closeModal = () => {
    setOpenStatus(false);
  };

  /**
   * Handle success notification.
   * @param {any} index - The index of the vehicle to delete.
   */
  const handleSuccess = async (index: number) => {
    try {
      await endConsumerApi.deleteVehicle(
        selectedEndConsumer?.userCustomId || '',
        vehicleList[index]._id,
      );
      if (selectedEndConsumer?.userCustomId) {
        await dispatch(
          fetchBasicInformations({ userCustomId: selectedEndConsumer.userCustomId }) as any,
        );
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message,
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
    setOpenStatus(true);
    setModalView({
      title: '',
      header: false,
      content: (
        <SuccessNotification
          title={t('vehicleDeletedSuccessfully')}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={() => {
            setVehicleList((previousData) =>
              previousData.filter((_, vehicleIndex) => vehicleIndex !== index),
            );
            setOpenStatus(false);
          }}
        />
      ),
      rightCorner: '',
      notification: true,
    });
  };

  /**
   * Handle delete vehicle.
   * @param {any} index - The index of the vehicle to delete.
   */
  const handleDeleteVehicle = (index: number) => {
    setOpenStatus(true);
    setModalView({
      title: '',
      header: false,
      content: (
        <DeleteWarning
          title={t('areYouSureYouWantToDeleteCar')}
          description={t('deletingYourCarIsPermanentAndCannotBeUndone')}
          cancelButtonLabel={t('cancel')}
          cancelButtonOnClick={closeModal}
          submitButtonLabel={t('delete')}
          submitButtonOnClick={() => handleSuccess(index)}
        />
      ),
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * Personal Charger
   * @param {string} vehicle - The vehicle
   * @param {string} type - The type
   */
  const handleTotalSessionsCharger = (vehicle: string, type: string) => {
    setIsModelOpen(true);
    setModelData({
      title: t('sessionDetails'),
      content: <SessionDetails vehicle={vehicle} type={type} />,
    });
  };

  /**
   * Render vehicle details.
   * @param {any} vehicle - The vehicle data.
   * @param {number} index - The index of the vehicle in the data array.
   * @returns {any} The rendered vehicle details.
   */
  const renderVehicleDetails = (
    vehicle: VehicleDetailsInterface & { originalIndex: number },
    index: number,
  ) => {
    return (
      <div className="vehicle-details">
        {isModelOpen && (
          <SidePaneModel
            open={isModelOpen}
            title={modelData.title}
            content={modelData.content}
            closePanel={() => setIsModelOpen(false)}
            preventQueryClear
          />
        )}
        <div className="vehicle-details-wrap">
          <div className="vehicle-details-header">
            <p className="vehicle-details-header-title">{t('commercial')}</p>
            <p className="vehicle-details-header-amount">
              {ToFixedValue(vehicle.commercialChargingDetails?.totalSessionAmount)}
              <p className="currency-icon">({branding.text.currency})</p>
            </p>
          </div>
          <div className="vehicle-details-content">
            <div className="vehicle-details-content-item">
              <p className="vehicle-details-content-item-title">{t('totalPowerConsumed')}</p>
              <p className="vehicle-details-content-item-value">
                <PowerIcon className="icons" />{' '}
                {ToFixedValue(
                  sessionCounts[vehicle._id]?.powerConsumedInKwh ??
                    vehicle.commercialChargingDetails?.totalPowerConsumedInKwh ??
                    0,
                )}
              </p>
            </div>
            <div className="vehicle-details-content-item">
              <p className="vehicle-details-content-item-title">{t('totalDuration')}</p>
              <div className="vehicle-details-content-item-value">
                <ClockIcon className="icons" />
                <span>
                  {FormatDuration(
                    sessionCounts[vehicle._id]?.durationSeconds ?? 
                    (vehicle.commercialChargingDetails?.totalDurationInMinutes ?? 0 ) * 60,
                    { formatBy: 'sec'},
                  )}
                </span>
              </div>
            </div>
            <div className="vehicle-details-content-item">
              <p className="vehicle-details-content-item-title">{t('noOfSessions')}</p>
              <p
                className="vehicle-details-content-item-value underline"
                role="presentation"
                onClick={() => handleTotalSessionsCharger(vehicle._id, 'commercial')}>
                {sessionCounts[vehicle._id]?.commercialCount !== undefined
                  ? sessionCounts[vehicle._id].commercialCount
                  : vehicle.commercialChargingDetails?.totalSessions || 0}
              </p>
            </div>
          </div>
        </div>
        {/* PERSONAL DETAILS AFTER WILL USE */}
        {/* <div className="vehicle-details-wrap">
          <div className="vehicle-details-header">
            <p className="vehicle-details-header-title">{t('personal')}</p>
          </div>
          <div className="vehicle-details-content">
            <div className="vehicle-details-content-item">
              <p className="vehicle-details-content-item-title">{t('totalPowerConsumed')}</p>
              <p className="vehicle-details-content-item-value">
                <PowerIcon className="icons" />{' '}
                {ToFixedValue(vehicle.personalChargingDetails?.totalPowerConsumedInKwh)}
              </p>
            </div>
            <div className="vehicle-details-content-item">
              <p className="vehicle-details-content-item-title">{t('totalDuration')}</p>
              <div className="vehicle-details-content-item-value">
                <ClockIcon className="icons " />
                <p>
                  {Math.floor((vehicle.personalChargingDetails?.totalDurationInMinutes || 0) / 60)}h
                </p>
                <span>
                  {Math.round((vehicle.personalChargingDetails?.totalDurationInMinutes || 0) % 60)}m
                </span>
              </div>
            </div>
            <div className="vehicle-details-content-item">
              <p className="vehicle-details-content-item-title">{t('noOfSessions')}</p>
              <p
                className="vehicle-details-content-item-value underline"
                role="presentation"
                onClick={() => handleTotalSessionsCharger(vehicle._id, 'personal')}>
                {sessionCounts[vehicle._id]?.personalCount !== undefined
                  ? sessionCounts[vehicle._id].personalCount
                  : vehicle.personalChargingDetails?.totalSessions || 0}
              </p>
            </div>
          </div>
        </div> */}
        <div className="action-buttons">
          <div className="action-toggle">
            <p
              style={{
                color: vehicle.isSelected ? 'var(--primary-color)' : 'var(--black-color)',
                fontSize: '12px',
              }}>
              {t('setAsPrimary')}
            </p>
            <SwitchButton
              checked={vehicle.isSelected}
              disabled={vehicle.isSelected}
              change={() => handleToggleSwitch('isSelected', vehicle.originalIndex)}
            />
          </div>
          <MyButton
            label={t('deleteVehicle')}
            buttonType="danger-outline"
            svgIcon={<TrashIcon className="icons" />}
            disableButton={vehicle.isSelected}
            disableClass={`action-delete${vehicle.isSelected ? ' action-delete--disabled' : ''}`}
            onClickFunc={() => handleDeleteVehicle(vehicle.originalIndex)}
          />
        </div>
        {index !== vehicleList.length - 1 && <div className="horizontal-line" />}
      </div>
    );
  };

  const sortedData = vehicleList
    .map((item, index) => ({ ...item, originalIndex: index }))
    .sort((a, b) => Number(b.isSelected) - Number(a.isSelected));

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    if (!basicInformation || basicInformation.userCustomId !== selectedEndConsumer.userCustomId) {
      dispatch(fetchBasicInformations({ userCustomId: selectedEndConsumer.userCustomId }) as any);
    }
  }, [selectedEndConsumer?.userCustomId, basicInformation]);

  useEffect(() => {
    if (basicInformation) {
      setVehicleList(basicInformation.vehicles || []);
      setIsLoading(false);
    }
  }, [basicInformation]);

  useEffect(() => {
    /**
     * Fetch session counts for vehicles
     */
    const fetchSessionCounts = async () => {
      const vehicleIds = vehicleList.map((vehicle) => vehicle._id).filter(Boolean);

      if (vehicleIds.length === 0) return;

      try {
        const response = await emspSessionApi.fetchVehiclesSessionCount(vehicleIds);
        const data: any = response?.data;
        let countList: any[] = [];
        if (Array.isArray(data)) {
          countList = data;
        } else if (Array.isArray(data?.data)) {
          countList = data.data;
        } else if (Array.isArray(data?.results)) {
          countList = data.results;
        }

        const countsMap: Record<
          string,
          {
            commercialCount: number;
            personalCount: number;
            totalCount: number;
            powerConsumedInKwh: number;
            durationSeconds: number;
          }
        > = {};
        countList.forEach((item: VehicleSessionCountInterface) => {
          if (item?.vehicleId) {
            countsMap[item.vehicleId] = {
              commercialCount: Number(item.commercialCount) || 0,
              personalCount: Number(item.personalCount) || 0,
              totalCount: Number(item.totalCount) || 0,
              powerConsumedInKwh: Number(item.powerConsumedInKwh) || 0,
              durationSeconds: Number(item.durationSeconds) || 0,
            };
          }
        });
        setSessionCounts(countsMap);
      } catch (error) {
        console.error('Failed to fetch vehicle session counts:', error);
      }
    };

    if (vehicleList.length > 0) {
      fetchSessionCounts();
    }
  }, [vehicleList]);

  return (
    <div className="vehicle-details-wrap">
      {isLoading ? (
        <div className="loader">
          <BatteryLoader />
        </div>
      ) : (
        <>
          <ModalBox
            open={openStatus}
            title={modalView.title}
            content={modalView.content}
            closeModal={closeModal}
            header={modalView.header}
            rightCorner={modalView.rightCorner}
          />
          <ConfirmBox
            title={confirmBox.title}
            open={confirmBox.status}
            closeModal={() => setConfirmBox({ ...confirmBox, status: false })}
            subTitle={confirmBox.subTitle}
            conformSubmit={confirmBox.conformSubmit}
            submitLabel={confirmBox.submitLabel}
          />
          <p className="vehicle-details-title">{t('vehicleDetails')}</p>
          {sortedData.map((vehicle, index) => (
            <CustomDropDown
              icon={(() => {
                const imgUrl =
                  vehicle.vehicleImageUrl ||
                  (vehicle.vehicleImageUrls && vehicle.vehicleImageUrls.length > 0
                    ? vehicle.vehicleImageUrls[0]?.url
                    : '');
                return imgUrl && imgUrl !== 'default' ? (
                  <img
                    className="vehicle-icon"
                    src={`${imgUrl}`}
                    alt={`${vehicle.manufacturer} ${vehicle.model}`}
                  />
                ) : (
                  <DefaultVehicleIcon className="vehicle-icon default-icon" />
                );
              })()}
              backgroundColor="plain"
              title={vehicle.manufacturer}
              description={vehicle.model}
              arrowPosition="right"
              height={50}
              content={renderVehicleDetails(vehicle as any, index)}
              open={index === 0}
            />
          ))}
        </>
      )}
    </div>
  );
}

export default MyVehicle;
